import { useState, useEffect } from 'react';
import moment from 'moment';
import DOMPurify from 'dompurify';
import dayjs from 'dayjs';
import {
    IoPersonAdd,
    IoClose,
    IoEyeOutline,
    IoCreateOutline,
    IoShieldCheckmark,
    IoChevronDown,
    IoChevronUp,
    IoPersonCircle,
    IoAdd,
    IoCheckmarkCircle
} from 'react-icons/io5';
import { BsPeople } from 'react-icons/bs';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Modal from '@mui/material/Modal';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import ToggleStyleSwitch from '@CommonRepresentationalDisplay/Switch/ToggleStyleSwitch';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import RequestService from '@Helpers/URLHelpers/RequestService';
import ErrorHandler from '@Root/ErrorHandler';
import './RequestAcceptForm.scss';

export default function RequestAcceptForm(props) {
    const className = 'requestAcceptForm';
    const requestService = new RequestService();
    const userInfoService = new UserInfoService();
    const userAuthService = new UserAuthService();
    const errorHandler = new ErrorHandler();
    const { activeUserId, setActiveUserAccessList } = useReduxActiveUser();

    const [cursor, setCursor] = useState('auto');
    const [usersList, setUsersList] = useState([]);
    const [userDetailsExpanded, setUserDetailsExpanded] = useState(false);
    const [dateOfJoining, setDateOfJoining] = useState(null);
    const [employmentLevel, setEmploymentLevel] = useState('');
    const [designation, setDesignation] = useState('');
    const [status, setStatus] = useState('');
    const [employeeId, setEmployeeId] = useState('');
    const [type, setType] = useState('');
    const [showAddReportingToUser, setShowAddReportingToUser] = useState(false);
    const [reportingToUser] = useState({fullName: 'N/A', userName:'N/A', id: -1});
    const [showReportingUserList, setShowReportingUsersList] = useState([]);
    const [reportingToUserList, setReportingToUserList] = useState([]);
    const [readPermission, setReadPermission] = useState(true);
    const [writePermission, setWritePermission] = useState(true);
    const [adminPermission, setAdminPermission] = useState(false);
    
    const colorList = ['#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060',
        '#b0ae60', '#65412b', '#cc9963', '#e1bd83', '#60748b', '#744700', '#c8d579', '#5f8fba', '#3e6073', '#8e6dc1', '#473785',
        '#9158a9', '#7f3f7a', '#583e84', '#073763', '#20124d', '#3d124d', '#64113f', '#780448', '#003e23', '#108764', '#1a4c3d',
        '#c8d77f', '#11888b', '#1a95bd', '#0d6c7b', '#25b56e', '#006e3d'];

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        try {
            let users = await userInfoService.getUserListByOrgId(props.request?.orgId);
            users = JSON.parse(users.body);
            setUsersList([{fullName: 'N/A', userName:'N/A', id: -1}, ...users]);
            setShowReportingUsersList([{fullName: 'N/A', userName:'N/A', id: -1}, ...users]);
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const handleReadPermissionsCheck = (e) => {
        let checked = e.target.checked;
        if(checked){
            setReadPermission(true);
        } else {
            setReadPermission(false);
            setWritePermission(false);
            setAdminPermission(false);
        }
    };

    const handleWritePermissionsCheck = (e) => {
        let checked = e.target.checked;
        if(checked && readPermission){
            setWritePermission(true);
        } else {
            setWritePermission(false);
            setAdminPermission(false);
        }
    };

    const handleAdminPermissionsCheck = (e) => {
        let checked = e.target.checked;
        if(checked && readPermission && writePermission){
            setAdminPermission(true);
        } else {
            setAdminPermission(false);
        }
    };

    const handleAcceptRequest = async (e) => {
        e.preventDefault();
        setCursor('wait');
        try {
            let request = props.request;
            let permissions = [];
            if(readPermission) {
                permissions.push('read');
                if(writePermission){
                    permissions.push('write');
                    if(adminPermission){
                        permissions.push('admin');
                    }
                }
            }
            request.permissions = JSON.stringify(permissions);
            let dateOfJoiningTimeStamp = dateOfJoining ? moment(dateOfJoining.$d).valueOf() : null;
            let userDetails = {
                employmentLevel: employmentLevel,
                employeeId: employeeId,
                status: status,
                designation: designation,
                dateOfJoining: dateOfJoiningTimeStamp,
                types: type,
                userId: request.requesterId,
                orgId: request.orgId
            };
            let userIds = reportingToUserList.map(item=> item.id);
            let bodyObj = {
                request: {
                    id: request.id,
                    requesterId: request.requesterId,
                    orgId: request.orgId,
                    teamId: request.teamId,
                    projectId: request.projectId,
                    requestType: request.requestType,
                    ownerId: request.ownerId,
                    permissions: request.permissions
                },
                reportingToUserIds: userIds,
                userDetails: userDetails
            };
            let response = await requestService.acceptRequest(bodyObj);
            response = JSON.parse(response.body);
            if(Object.keys(response || {}).length !== 0) {
                let userAccessList = await userAuthService.getAccessListForUser(activeUserId);
                userAccessList = JSON.parse(userAccessList.body);
                setActiveUserAccessList(userAccessList);
                await props.reloadData();
                props.closePopup();
            }
        } catch (err) {
            props.errorHandler();
        }
        setCursor('auto');
    };

    const handleReportingUserChange = (e, value) => {
        if(value !== null) {
            setReportingToUserList(prev=>[...prev, value]);
            let filter = showReportingUserList.filter(item=>item.id !== value.id);
            setShowReportingUsersList(filter);
            setShowAddReportingToUser(false);
        }
    };

    const handleDeleteReportingToUser = (id) => {
        let getUser = usersList.find(item=> item.id === id);
        setReportingToUserList(prev=> prev.filter(item=>item.id !== id));
        setShowReportingUsersList(prev=> [...prev, getUser]);
    };

    const getInitials = (string) => {
        if(!!string && string.length > 0) {
            var names = string?.split(' '),
                initials = names[0]?.substring(0, 1)?.toUpperCase();

            if (names?.length > 1) {
                initials += names[names?.length - 1]?.substring(0, 1)?.toUpperCase();
            } else {
                names = string?.split('@');
                initials = names[0]?.substring(0, 1)?.toUpperCase();

                if (names?.length > 1) {
                    initials += names[names?.length - 1]?.substring(0, 1)?.toUpperCase();
                }
            }
            return initials;
        }
        return 'N/A';
    };

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    };

    const employmentTypes = [
        { value: 'fullTime', label: 'Full Time Employment' },
        { value: 'partTime', label: 'Part Time Employment' },
        { value: 'apprenticeship', label: 'Apprenticeship' },
        { value: 'traineeship', label: 'Traineeship' },
        { value: 'internship', label: 'Internship' },
        { value: 'casualEmployment', label: 'Casual Employment' },
        { value: 'contractEmployment', label: 'Contract Employment' },
        { value: 'seasonalEmployment', label: 'Seasonal Employment' },
        { value: 'leasedEmployment', label: 'Leased Employment' },
        { value: 'contingentEmployment', label: 'Contingent Employment' },
        { value: 'probation', label: 'Probation' }
    ];

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}
            className={className}>
            <div className={`${className}__container`}>
                {/* Header */}
                <div className={`${className}__header`}>
                    <div className={`${className}__header__icon`}>
                        <IoPersonAdd />
                    </div>
                    <div className={`${className}__header__content`}>
                        <h2 className={`${className}__header__title`}>Accept Request</h2>
                        <p className={`${className}__header__subtitle`}>
                            {props.request.requestType === 'org' 
                                ? `${props?.request?.requesterFullName} wants to join ${props?.request?.orgName}`
                                : `${props?.request?.requesterFullName} wants to join ${props?.request?.teamName}`
                            }
                        </p>
                    </div>
                    <IconButton 
                        onClick={props?.closePopup}
                        className={`${className}__header__close`}
                    >
                        <IoClose />
                    </IconButton>
                </div>

                <form className={`${className}__form`} onSubmit={handleAcceptRequest}>
                    {/* Permissions Section */}
                    <div className={`${className}__section`}>
                        <div className={`${className}__section__header`}>
                            <IoShieldCheckmark />
                            <h3>Access Permissions</h3>
                        </div>

                        <div className={`${className}__permissions`}>
                            {/* Read Permission */}
                            <div className={`${className}__permission-card ${readPermission ? `${className}__permission-card--active` : ''}`}>
                                <div className={`${className}__permission-card__icon ${className}__permission-card__icon--read`}>
                                    <IoEyeOutline />
                                </div>
                                <div className={`${className}__permission-card__content`}>
                                    <div className={`${className}__permission-card__header`}>
                                        <div>
                                            <h4>View All</h4>
                                            <p>
                                                {props.request.requestType === 'org'
                                                    ? "Can see organization's members, teams and projects"
                                                    : "Can see team's members and projects"
                                                }
                                            </p>
                                        </div>
                                        <ToggleStyleSwitch
                                            onChange={handleReadPermissionsCheck}
                                            checked={readPermission}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Write Permission */}
                            <div className={`${className}__permission-card ${writePermission && readPermission ? `${className}__permission-card--active` : ''}`}>
                                <div className={`${className}__permission-card__icon ${className}__permission-card__icon--write`}>
                                    <IoCreateOutline />
                                </div>
                                <div className={`${className}__permission-card__content`}>
                                    <div className={`${className}__permission-card__header`}>
                                        <div>
                                            <h4>Create & Edit</h4>
                                            <p>
                                                {props.request.requestType === 'org'
                                                    ? "Can create and edit projects and teams"
                                                    : "Can edit team details"
                                                }
                                            </p>
                                        </div>
                                        <ToggleStyleSwitch
                                            onChange={handleWritePermissionsCheck}
                                            checked={writePermission && readPermission}
                                            disabled={!readPermission}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Admin Permission */}
                            <div className={`${className}__permission-card ${adminPermission && writePermission && readPermission ? `${className}__permission-card--active` : ''}`}>
                                <div className={`${className}__permission-card__icon ${className}__permission-card__icon--admin`}>
                                    <IoShieldCheckmark />
                                </div>
                                <div className={`${className}__permission-card__content`}>
                                    <div className={`${className}__permission-card__header`}>
                                        <div>
                                            <h4>Admin Privileges</h4>
                                            <p>
                                                {props.request.requestType === 'org'
                                                    ? "Can invite users, manage permissions, and handle requests"
                                                    : "Can invite users, manage team permissions, and handle requests"
                                                }
                                            </p>
                                        </div>
                                        <ToggleStyleSwitch
                                            onChange={handleAdminPermissionsCheck}
                                            checked={adminPermission && writePermission && readPermission}
                                            disabled={!writePermission}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* User Details Section (Only for Org) */}
                    {props.request.requestType === 'org' && (
                        <>
                            <div className={`${className}__section`}>
                                <div 
                                    className={`${className}__section__header ${className}__section__header--collapsible`}
                                    onClick={() => setUserDetailsExpanded(!userDetailsExpanded)}
                                >
                                    <div className={`${className}__section__header__left`}>
                                        <IoPersonCircle />
                                        <h3>User Details</h3>
                                        <span className={`${className}__section__header__badge`}>Optional</span>
                                    </div>
                                    {userDetailsExpanded ? <IoChevronUp /> : <IoChevronDown />}
                                </div>

                                <Collapse in={userDetailsExpanded}>
                                    <div className={`${className}__user-details`}>
                                        <div className={`${className}__form-row`}>
                                            <div className={`${className}__form-field`}>
                                                <label>Employment Level</label>
                                                <TextField
                                                    placeholder='Enter level (e.g., 3)'
                                                    type='number'
                                                    size='small'
                                                    fullWidth
                                                    value={employmentLevel}
                                                    onChange={(e) => setEmploymentLevel(DOMPurify.sanitize(e.target.value))}
                                                    InputProps={{
                                                        sx: {
                                                            borderRadius: '10px',
                                                            backgroundColor: '#f8fafc'
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div className={`${className}__form-field`}>
                                                <label>Employee ID</label>
                                                <TextField
                                                    placeholder='Enter employee ID'
                                                    size='small'
                                                    fullWidth
                                                    value={employeeId}
                                                    onChange={(e) => setEmployeeId(DOMPurify.sanitize(e.target.value))}
                                                    InputProps={{
                                                        sx: {
                                                            borderRadius: '10px',
                                                            backgroundColor: '#f8fafc'
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className={`${className}__form-row`}>
                                            <div className={`${className}__form-field`}>
                                                <label>Status</label>
                                                <TextField
                                                    placeholder='e.g., Active, Probation'
                                                    size='small'
                                                    fullWidth
                                                    value={status}
                                                    onChange={(e) => setStatus(DOMPurify.sanitize(e.target.value))}
                                                    InputProps={{
                                                        sx: {
                                                            borderRadius: '10px',
                                                            backgroundColor: '#f8fafc'
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div className={`${className}__form-field`}>
                                                <label>Designation</label>
                                                <TextField
                                                    placeholder='e.g., Software Engineer'
                                                    size='small'
                                                    fullWidth
                                                    value={designation}
                                                    onChange={(e) => setDesignation(DOMPurify.sanitize(e.target.value))}
                                                    InputProps={{
                                                        sx: {
                                                            borderRadius: '10px',
                                                            backgroundColor: '#f8fafc'
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className={`${className}__form-row`}>
                                            <div className={`${className}__form-field`}>
                                                <label>Date of Joining</label>
                                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-in'}>
                                                    <DateTimePicker
                                                        format='DD/MM/YYYY hh:mm a'
                                                        value={!!dateOfJoining ? dayjs(dateOfJoining) : null}
                                                        onChange={(date) => setDateOfJoining(date)}
                                                        slotProps={{
                                                            textField: {
                                                                size: 'small',
                                                                fullWidth: true,
                                                                InputProps: {
                                                                    sx: {
                                                                        borderRadius: '10px',
                                                                        backgroundColor: '#f8fafc'
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </div>

                                            <div className={`${className}__form-field`}>
                                                <label>Employment Type</label>
                                                <Select
                                                    value={type}
                                                    onChange={(e) => setType(e.target.value)}
                                                    size='small'
                                                    fullWidth
                                                    displayEmpty
                                                    sx={{
                                                        borderRadius: '10px',
                                                        backgroundColor: '#f8fafc'
                                                    }}
                                                >
                                                    <MenuItem value='' disabled>Select type</MenuItem>
                                                    {employmentTypes.map(emp => (
                                                        <MenuItem key={emp.value} value={emp.value}>
                                                            {emp.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                </Collapse>
                            </div>

                            {/* Reporting To Section */}
                            <div className={`${className}__section`}>
                                <div className={`${className}__section__header`}>
                                    <BsPeople />
                                    <h3>Reporting Structure</h3>
                                </div>

                                <div className={`${className}__reporting`}>
                                    {reportingToUserList.length > 0 && (
                                        <div className={`${className}__reporting__list`}>
                                            {reportingToUserList.map((user, id) => (
                                                <Chip
                                                    key={id}
                                                    label={`${user.fullName} (@${user.userName})`}
                                                    variant='outlined'
                                                    color='primary'
                                                    size='medium'
                                                    onDelete={() => handleDeleteReportingToUser(user.id)}
                                                    avatar={
                                                        user.photo !== undefined && user.photo !== null
                                                            ? <Avatar src={URL.createObjectURL(dataURLtoFile(user.photo))} />
                                                            : <Avatar sx={{ bgcolor: colorList[user.fullName?.charCodeAt(0) % colorList.length] + ' !important' }}>
                                                                {getInitials(user.fullName)}
                                                              </Avatar>
                                                    }
                                                    className={`${className}__reporting__chip`}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {showAddReportingToUser ? (
                                        <Autocomplete
                                            options={showReportingUserList}
                                            value={reportingToUser}
                                            disabled={cursor === 'wait'}
                                            getOptionLabel={(option) => `${option.fullName} (@${option.userName})`}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            onChange={handleReportingUserChange}
                                            renderInput={(params) => (
                                                <TextField 
                                                    {...params} 
                                                    placeholder='Search for a user...' 
                                                    variant='outlined'
                                                    size='small'
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        sx: {
                                                            borderRadius: '10px',
                                                            backgroundColor: '#f8fafc'
                                                        }
                                                    }}
                                                />
                                            )}
                                            className={`${className}__reporting__autocomplete`}
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            className={`${className}__reporting__add-btn`}
                                            onClick={() => setShowAddReportingToUser(true)}
                                        >
                                            <IoAdd />
                                            Add Reporting Manager
                                        </button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Actions */}
                    <div className={`${className}__actions`}>
                        <button
                            type="button"
                            className={`${className}__actions__cancel`}
                            onClick={props.closePopup}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`${className}__actions__submit`}
                            disabled={cursor === 'wait'}
                        >
                            {cursor === 'wait' ? (
                                <>
                                    <div className={`${className}__spinner`}></div>
                                    Accepting...
                                </>
                            ) : (
                                <>
                                    <IoCheckmarkCircle />
                                    Accept Request
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
}
